import * as React from 'react';
import { styles } from './styles';
import { components } from 'react-select';
import Createable from 'react-select/lib/Creatable';
import { Subject, Subscription } from 'rxjs';
import { DropdownIndicator, IndicatorProps } from 'react-select/lib/components/indicators';
import { ControlProps } from 'react-select/lib/components/Control';
import { SelectComponents } from 'react-select/lib/components';
import { debounceTime } from 'rxjs/operators';
import { ControlGroup } from '@withjoy/joykit';
import { Analytics } from '@withjoy/web-core';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import tzlookup from 'tz-lookup';
import { onLocationSelectType } from '../../../../types';
import { trimStart } from '../../../../../../../../utils/strings';

interface DropdownChoice {
  label: string;
  value: string;
}

interface Props {
  inGroup?: boolean;
  value: string;
  required?: boolean;
  onSelected: onLocationSelectType;
  touchscreen?: boolean;
  analytics: Analytics;
  error: boolean;
  handleTimezoneChange: (method: string) => void;
}

interface State {
  error?: string;
  query: string;
  results: DropdownChoice[];
}

const autocompleteService: google.maps.places.AutocompleteService = new google.maps.places.AutocompleteService();
const googleLogo = require('@assets/powered_by_google.png');

class LocationPicker extends React.Component<Props, State> {
  onQuery$?: Subject<string> = new Subject();
  querySubscription?: Subscription;
  outputRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);

    this.state = {
      query: props.value,
      results: []
    };
  }

  componentDidMount() {
    if (this.onQuery$) {
      this.querySubscription = this.onQuery$.pipe(debounceTime(500)).subscribe(debounced => {
        this.fetchPredictions(debounced);
      });
    }
  }

  componentWillUnmount() {
    if (this.querySubscription) {
      this.querySubscription.unsubscribe();
    }
  }

  fetchPredictions = (query: string) => {
    const request: google.maps.places.AutocompletionRequest = {
      input: query || '',
      types: ['(cities)']
    };

    if (query === '') {
      this.setState({ results: [] });
      return;
    }

    autocompleteService.getPlacePredictions(request, (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        return;
      }

      const options = predictions.map(({ description, place_id }) => ({
        label: description,
        value: description,
        placeId: place_id
      }));

      this.setState({ results: options });
    });
  };

  handleInputChange = (query: string) => {
    query = trimStart(query) || '';
    this.setState({ query });
    if (this.onQuery$) {
      this.onQuery$.next(query);
    }
    return query;
  };

  handleChange = (choice: any) => {
    if (choice && choice.placeId) {
      this.props.analytics.track({ action: 'Location', actionType: 'select' });
      geocodeByAddress(choice.value)
        .then(results => getLatLng(results[0]))
        .then(latLng => {
          let tzid;
          try {
            tzid = tzlookup(latLng.lat, latLng.lng);
            this.props.handleTimezoneChange('google');
          } catch (e) {
            this.props.analytics.trackError({
              error: 'TimezoneRegistrationFailed',
              attemptedAction: 'register timezone',
              errorDescription: e.message
            });
          }
          this.props.onSelected(choice.value, latLng.lat, latLng.lng, choice.placeId, tzid);
        })
        .catch(error => {
          this.props.analytics.trackError({
            error: 'LocationSelectedFailed',
            attemptedAction: 'location select',
            errorDescription: error.message
          });
        });
    } else if (choice && !choice.placeId) {
      this.props.onSelected(choice.value, null, null, null, null);
    } else {
      // what is this for?
      this.props.onSelected(undefined, null, null, null, null);
    }
  };

  renderSelectComponents = () => {
    const mediaQueryList = window.matchMedia('(hover: none)');

    const selectComponents: Partial<SelectComponents<DropdownChoice>> = {};
    if (mediaQueryList.matches) {
      selectComponents.Control = this.renderMobileControl;
      selectComponents.DropdownIndicator = this.renderMobileDropdownIndicator;
    }
    return selectComponents;
  };

  render() {
    const { value } = this.props;
    const defaultValue = value && value.length ? { value, label: value } : null;
    const { results } = this.state;
    const error = this.props.error ? 'Please choose a location' : undefined;
    return (
      <>
        <ControlGroup label="Where?" error={error}>
          <Createable
            openMenuOnClick={true}
            placeholder={'e.g. San Francisco, CA'}
            formatCreateLabel={this.formatCreateLabel}
            options={results.length ? results : []}
            styles={{
              control: base => styles.control(base, this.props.inGroup),
              valueContainer: base => styles.valueContainer(base, this.props.inGroup),
              dropdownIndicator: () => ({ display: 'none' }),
              indicatorSeparator: () => ({ display: 'none' })
            }}
            isClearable={true}
            onInputChange={this.handleInputChange}
            onChange={this.handleChange}
            value={defaultValue}
            components={this.renderSelectComponents()}
          />
        </ControlGroup>

        <div className={styles.logoContainer}>
          <img src={googleLogo} />
        </div>
      </>
    );
  }

  private formatCreateLabel = (inputValue: string) => `Use '${inputValue}'`;

  private renderMobileControl = ({ innerProps, ...props }: ControlProps<DropdownChoice>) => {
    const innerPropsClone = { ...innerProps };
    innerPropsClone.onMouseDown = e => {
      e.preventDefault();
    };
    return <components.Control innerProps={innerPropsClone} {...props} />;
  };

  private renderMobileDropdownIndicator = ({ innerProps, ...props }: IndicatorProps<DropdownChoice>) => {
    const innerPropsClone = { ...innerProps };
    innerPropsClone.onMouseDown = (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
    };
    return <DropdownIndicator innerProps={innerPropsClone} {...props} />;
  };
}

export { LocationPicker };
